import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import LocationsList from './components/LocationsList';
import QuestMap from './components/QuestMap';
import QuestSelector from './components/QuestSelector';
import QuestInfoEditor from './components/QuestInfoEditor';
import PromoEditor from './components/PromoEditor';
import AppPreview from './components/AppPreview';
import PriceEditor from './components/PriceEditor';
import OpensAtEditor from './components/OpensAtEditor';
import Login from './components/Login';
import AudioStats from './components/AudioStats';
import SpeechGeneration from './components/SpeechGeneration';
import TagsSummary from './components/TagsSummary';
import AudioStudio from './components/AudioStudio';
import QuestDiffViewer from './components/QuestDiffViewer';
import CopyQuestJsonModal from './components/CopyQuestJsonModal'; // Import the new modal
import ExportMarkdownModal from './components/ExportMarkdownModal';
import NewQuestModal from './components/NewQuestModal';
import ImportQuestModal from './components/ImportQuestModal';
import ErrorBoundary from './components/ErrorBoundary';
import AdminPanel from './components/AdminPanel';
import PlatformListings from './components/PlatformListings';
import BgProcesses from './components/BgProcesses';
import TasksScreen from './components/Tasks/TasksScreen';
import ImageUpscaler from './components/ImageUpscaler';
import PromptBuilder from './components/PromptBuilder';
import PipelinePanel from './components/Pipeline/PipelinePanel';
import PipelineLocationControls from './components/Pipeline/PipelineLocationControls';
import PipelineNotifications from './components/Pipeline/PipelineNotifications';
import BatchPanels from './components/Pipeline/BatchPanels';
import { StageLegend } from './components/Pipeline/pipelineStage';
import { v4 as uuidv4 } from 'uuid';
import { fastScrollToElement } from './utils/scroll';
import { ALL_RECIPIENTS, PREDEFINED_TAGS } from './lensSections';
import { exportQuestToPdf } from './utils/exportPdf';

// --- URL <-> selection mapping -------------------------------------------
// The address bar mirrors the current environment + quest as /<env>/<questId>
// (e.g. /test/unordinary-istanbul or /prod/unordinary-istanbul), so a quest
// can be re-opened just by re-entering the same URL, and back/forward function as expected.
const TABLE_TO_SLUG = { quests_test: 'test', quests: 'prod' };
const SLUG_TO_TABLE = { test: 'quests_test', prod: 'quests' };

function parseLocationToSelection() {
  if (typeof window === 'undefined') return {};
  const parts = window.location.pathname.split('/').filter(Boolean);
  const table = SLUG_TO_TABLE[parts[0]];
  if (!table) return {};
  return { table, questId: parts[1] ? decodeURIComponent(parts[1]) : '' };
}

function buildSelectionPath(table, questId) {
  const slug = TABLE_TO_SLUG[table] || 'test';
  return questId ? `/${slug}/${encodeURIComponent(questId)}` : `/${slug}`;
}

// The pipeline tab has its own address: /pipeline or /pipeline/<runId>, so a
// refresh lands back in the pipeline (optionally on the open run) — not the editor.
function parsePipelinePath() {
  if (typeof window === 'undefined') return { pipeline: false, runId: null };
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (parts[0] === 'pipeline') return { pipeline: true, runId: parts[1] ? decodeURIComponent(parts[1]) : null };
  return { pipeline: false, runId: null };
}

// The platform-listings page has its own address, /platforms, so it can be
// linked to and survives a reload — same as the pipeline tab.
function parsePlatformsPath() {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.split('/').filter(Boolean)[0] === 'platforms';
}

// The background-processes page (admin) lives at /processes, same idea.
function parseProcessesPath() {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.split('/').filter(Boolean)[0] === 'processes';
}

// The Tasks screen of a quest lives at /<env>/<questId>/tasks?task=<id>&lens=…
// &state=…&group=point&point=<uuid>&view=turns&field=<field>&turn=<uuid>.<field>.<i>:
// a link to a card opens that card, `view=turns` opens the by-turns view (on
// the point/field when given, with one speaker turn open on the right when
// `turn` names it), and a reload keeps the view.
const TASKS_KEYS = ['task', 'lens', 'state', 'group', 'point', 'view', 'field', 'turn', 'turns'];
function parseTasksPath() {
  if (typeof window === 'undefined') return null;
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (!SLUG_TO_TABLE[parts[0]] || parts[2] !== 'tasks') return null;
  const q = new URLSearchParams(window.location.search);
  const st = {};
  TASKS_KEYS.forEach((k) => { const v = q.get(k); if (v) st[k] = v; });
  return st;
}
function buildTasksPath(table, questId, st) {
  const q = new URLSearchParams();
  TASKS_KEYS.forEach((k) => { if (st && st[k]) q.set(k, st[k]); });
  const search = q.toString();
  return `${buildSelectionPath(table, questId)}/tasks${search ? `?${search}` : ''}`;
}

// Full-screen views of the editor that live in the address bar as ?view=<name>
// next to ?location=, so a refresh or a link in a new tab opens the same view.
const VIEW_INFO = 'info';
const VIEW_AUDIO_STATS = 'audio-stats';
const VIEW_TAGS = 'tags';

function parseViewFromUrl() {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('view');
}

// Give every location the two identifiers the editor relies on: a uuid and a
// numeric id. Imported route skeletons have neither, and the editor keys
// selection, scrolling and its React lists off `id`. Missing ids are filled
// with values above the current max so they never collide with existing ones.
// Mutates in place (callers already treat data.locations as mutable).
function normalizeLocationsInPlace(locations) {
  if (!Array.isArray(locations) || locations.length === 0) return locations;
  let maxId = 0;
  locations.forEach((loc) => {
    const n = typeof loc.id === 'number' ? loc.id : parseInt(loc.id, 10);
    if (Number.isFinite(n)) maxId = Math.max(maxId, n);
  });
  locations.forEach((loc) => {
    if (!loc.uuid) loc.uuid = uuidv4();
    const n = typeof loc.id === 'number' ? loc.id : parseInt(loc.id, 10);
    if (!Number.isFinite(n)) loc.id = ++maxId;
  });
  return locations;
}

function App() {
  const [questInfo, setQuestInfo] = useState(null);
  const [locations, setLocations] = useState([]);
  // Location comment threads, keyed by location_uuid: { [uuid]: [message, ...] }.
  // Loaded separately from quest_data (comments live in their own table).
  const [commentsByLocation, setCommentsByLocation] = useState({});
  // Editor logins, for picking a tag's recipient(s).
  const [usernames, setUsernames] = useState([]);
  const [questsList, setQuestsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // Initial width in percentage
  const [selectedQuestId, setSelectedQuestId] = useState(
    () => parseLocationToSelection().questId || localStorage.getItem('lastQuestId') || ''
  );
  const [selectedTable, setSelectedTable] = useState(
    () => parseLocationToSelection().table || localStorage.getItem('selectedTable') || 'quests_test'
  );
  const [currentUser, setCurrentUser] = useState(null); // { username, isAdmin, canPushToProd }
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showImageUpscaler, setShowImageUpscaler] = useState(false);
  const [showPromptBuilder, setShowPromptBuilder] = useState(false);
  const [showPipeline, setShowPipeline] = useState(() => parsePipelinePath().pipeline);
  const [showPlatforms, setShowPlatforms] = useState(() => parsePlatformsPath());
  const [showProcesses, setShowProcesses] = useState(() => parseProcessesPath());
  // uuid -> {open, total}: how many Tasks cards point at each location, for
  // the chips in the editor and the studio. Reloaded on every task event.
  const [taskCountsByLocation, setTaskCountsByLocation] = useState(null);
  // Tasks screen: open flag plus its view state (the open card, lens, state filter…).
  const [showTasks, setShowTasks] = useState(() => !!parseTasksPath());
  const [tasksState, setTasksState] = useState(() => parseTasksPath() || {});
  const [pipelineRunId, setPipelineRunId] = useState(() => parsePipelinePath().runId);
  // Pipeline draft: if the selected quest is a generation draft, this holds the
  // per-location generation meta (keyed by uuid). null for normal quests.
  const [pipelineMeta, setPipelineMeta] = useState(null);
  const [pipelineRefresh, setPipelineRefresh] = useState(0);
  // Lighter sibling of pipelineRefresh: bumping it reloads ONLY the pipeline
  // meta (per-location status chips), NOT the whole quest_data. Used for
  // pipeline-state-only changes (e.g. picking a task candidate) so the editor
  // is not blanked by the "Loading quest data…" screen on every such action.
  const [pipelineMetaRefresh, setPipelineMetaRefresh] = useState(0);
  // Lens: which location-card sections are visible (view-only filter). Persisted
  // like the theme so it survives reloads. {} = show everything.
  const [lens, setLens] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lens') || '{}') || {}; } catch (e) { return {}; }
  });
  const handleLensChange = useCallback((next) => {
    setLens(next || {});
    try { localStorage.setItem('lens', JSON.stringify(next || {})); } catch (e) {}
  }, []);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'success', 'error'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Unsaved text in the Promo panel (its own save, straight to prod).
  const [promoDirty, setPromoDirty] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [username, setUsername] = useState(null);
  const [showQuestInfoEditor, setShowQuestInfoEditor] = useState(() => parseViewFromUrl() === VIEW_INFO);
  const [showAudioStats, setShowAudioStats] = useState(() => parseViewFromUrl() === VIEW_AUDIO_STATS);
  const [showTagsSummary, setShowTagsSummary] = useState(() => parseViewFromUrl() === VIEW_TAGS);
  // The studio lives in the address bar: ?studio=<tab>&field=<field> alongside
  // the usual ?location=<uuid>. So a refresh, a bookmark and a shared link all
  // land on the same point, field and tab.
  const studioFromUrl = () => {
    if (typeof window === 'undefined') return null;
    const q = new URLSearchParams(window.location.search);
    const tab = q.get('studio');
    // ?draft=<id>&back=<path>: the Tasks screen opened the studio to edit INTO a Draft of a turn
    const draft = q.get('draft') ? { id: q.get('draft'), back: q.get('back') || null } : null;
    return tab ? { tab, field: q.get('field') || 'description', version: Number(q.get('version')) || null, draft } : null;
  };
  const [showAudioStudio, setShowAudioStudio] = useState(() => !!studioFromUrl());
  const [studioEntry] = useState(studioFromUrl);      // only the first read matters
  const [studioState, setStudioState] = useState(null);
  const [showDiffViewer, setShowDiffViewer] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false); // State for the new modal
  // Markdown export modal. `null` = closed; otherwise { mode: 'quest' } or
  // { mode: 'location', location, index } for a single location.
  const [markdownExport, setMarkdownExport] = useState(null);
  const [showNewQuestModal, setShowNewQuestModal] = useState(false);
  const [showImportQuestModal, setShowImportQuestModal] = useState(false);
  const [rightPanelView, setRightPanelView] = useState('map'); // 'map' or 'speech'
  // What the Quest Info panel's editors report for the app preview (right pane).
  const [previewPromo, setPreviewPromo] = useState(null);
  const [previewPrice, setPreviewPrice] = useState(null);
  const [previewOpensAt, setPreviewOpensAt] = useState(null);
  // Price and pre-order date come as { test, prod } rows; the preview shows the
  // one of the table being edited.
  const previewRow = (d) => (d ? d[selectedTable === 'quests' ? 'prod' : 'test'] : null);
  const [speechGenInitialData, setSpeechGenInitialData] = useState(null);
  // Bumped when SpeechGeneration uploads a new voice-over version so
  // LocationsList re-fetches its audio metadata.
  const [audioRefreshToken, setAudioRefreshToken] = useState(0);
  const [locationToSelect, setLocationToSelect] = useState(() => {
    // A location uuid in the URL (?location=) wins over the last-seen point.
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('location')) {
      return null;
    }
    const lastLocationId = localStorage.getItem('lastLocationId');
    return lastLocationId ? parseInt(lastLocationId, 10) : null;
  });
  // Location uuid pulled from the URL (?location=), resolved to a point once the
  // quest's locations have loaded. Takes precedence over the localStorage point.
  const [pendingLocationUuid, setPendingLocationUuid] = useState(
    () => (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('location')
      : null)
  );
  const locationsListRef = useRef(null);
  // Timestamp (ms) until which the scroll-spy is suppressed, so a programmatic
  // scroll-to-selected doesn't flicker the highlight through passed-over points.
  const suppressSpyUntil = useRef(0);
  const isDraggingRef = useRef(false);
  // Width picked up during a divider drag. Held in a ref (and written straight
  // to the node) instead of state, so a drag does not re-render the whole point
  // list on every mouse move; state is set once when the drag ends.
  const dragWidthRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const prevQuestIdRef = useRef();
  const prevPipelineRefreshRef = useRef(0);
  const sseClientIdRef = useRef(null);

  const [isNarrowLayout, setIsNarrowLayout] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= 1024
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1024px)');
    const handler = () => setIsNarrowLayout(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Check authentication status on initial load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/api/auth-status', {
          credentials: 'include' // Important for cookies
        });

        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(true);
          setCurrentUser({
            username: data.username,
            isAdmin: !!data.isAdmin,
            canPushToProd: !!data.canPushToProd,
          });
          if (!data.isAdmin && selectedTable !== 'quests_test') {
            setSelectedTable('quests_test');
          }
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('Error checking auth status:', err);
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuthStatus();
  }, []);

  // --- SSE Connection Handling ---
  // Bumping the epoch tears the EventSource down and rebuilds it from scratch —
  // needed when the browser gives up on the stream for good (an HTTP error on
  // reconnect, e.g. a 502 while the backend restarts, closes it permanently).
  const [sseEpoch, setSseEpoch] = useState(0);
  // Streams rebuilt in a row that brought nothing at all (App's watchdog below).
  const sseSilentRebuildsRef = useRef(0);
  useEffect(() => {
    if (!selectedQuestId || !isAuthenticated) {
      return;
    }

    // Connect to the correct SSE endpoint based on the hostname.
    // If on localhost, connect directly to the backend on port 5000 to bypass the CRA proxy.
    // Otherwise, use the same host/port the page was loaded from and let the reverse proxy (e.g., Nginx) handle routing.
    let eventSourceUrl;
    if (window.location.hostname === 'localhost') {
      eventSourceUrl = `http://localhost:5000/api/events/${selectedQuestId}`;
    } else {
      // Use a relative URL on the production server.
      // This will correctly use https if the main page is loaded via https.
      eventSourceUrl = `/api/events/${selectedQuestId}`;
    }

    console.log(`🚀 Setting up SSE to: ${eventSourceUrl}`);
    const eventSource = new EventSource(eventSourceUrl, { withCredentials: true });

    // Events missed while the connection was down are gone (no replay on this
    // channel), so every REconnect refetches the quest — pipeline-written fields
    // and edits from other tabs appear without a manual page reload.
    const resync = async () => {
      try {
        const r = await fetch(`/api/quest/${selectedQuestId}?table=${selectedTable}`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        normalizeLocationsInPlace(data.locations);
        setQuestInfo(data.quest_info || {});
        setLocations(data.locations || []);
      } catch (e) { /* transient — the next resync will catch up */ }
      // Screens with data of their own (Tasks) read it again too.
      window.dispatchEvent(new CustomEvent('qv:resync', { detail: { questId: selectedQuestId } }));
    };

    let connectedBefore = false;
    let reconnectTimer = null;
    // The state of the stream for the screens that show it (the Tasks chat
    // says «no connection» instead of looking idle): 'open' | 'lost' | 'poll'.
    // 'poll': the stream opens but nothing comes through it (something on the
    // way holds it); the page stops rebuilding it and the screens read their
    // data on a timer instead (`qv:poll`, below) — no warning: the page stays current.
    const setStream = (state) => {
      if (window.__qvStream === state) return;
      window.__qvStream = state;
      window.dispatchEvent(new CustomEvent('qv:stream', { detail: { state } }));
    };
    eventSource.onopen = () => {
      console.log('SSE connection opened.');
      lastBeat = Date.now();
      if (window.__qvStream !== 'poll') setStream('open');
      if (connectedBefore) resync();
      connectedBefore = true;
    };

    // A stream can die without the browser noticing (a sleeping laptop, a
    // Wi-Fi change, a proxy dropping the connection): it stays «open» and
    // nothing arrives until F5. The server sends a `ping` every 10 s; when
    // nothing at all has come for STREAM_SILENCE_MS, the stream is rebuilt
    // (and the reconnect resyncs what was missed).
    const STREAM_SILENCE_MS = 25000;
    let lastBeat = Date.now();
    const POLL_MS = 4000;
    let gotAny = false;       // this stream brought at least one event
    let pollTimer = null;
    const startPolling = () => {
      if (pollTimer) return;
      console.warn('SSE: the stream opens but brings nothing — reading the screens\' data on a timer instead');
      setStream('poll');
      pollTimer = setInterval(() => window.dispatchEvent(new CustomEvent('qv:poll', { detail: { questId: selectedQuestId } })), POLL_MS);
    };
    const heard = () => {
      lastBeat = Date.now();
      if (!gotAny) { gotAny = true; sseSilentRebuildsRef.current = 0; }
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; resync(); }
      setStream('open');
    };
    eventSource.addEventListener('ping', heard);
    const watchdog = setInterval(() => {
      if (pollTimer || Date.now() - lastBeat < STREAM_SILENCE_MS) return;
      // A second silent stream in a row is not a dropped connection: it is
      // held on the way. Keep it open (it may start to flow) and poll.
      if (!gotAny && sseSilentRebuildsRef.current >= 1) { startPolling(); return; }
      if (!gotAny) sseSilentRebuildsRef.current += 1;
      console.warn(`SSE: nothing for ${Math.round((Date.now() - lastBeat) / 1000)} s — rebuilding the stream`);
      setStream('lost');
      setSseEpoch((n) => n + 1);
    }, 5000);
    // Already polling (a stream rebuilt for another reason, another quest):
    // keep the screens fed from the start instead of after another silence.
    if (window.__qvStream === 'poll') startPolling();

    // A sleeping tab can miss both events and the connection's death: on wake,
    // rebuild a dead stream or just catch up on what the open one missed.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (eventSource.readyState === EventSource.CLOSED) setSseEpoch((n) => n + 1);
      else resync();
    };
    document.addEventListener('visibilitychange', onVisible);

    eventSource.onmessage = (event) => {
      heard();
      const data = JSON.parse(event.data);
      if (data.type !== 'task_live') console.log('📬 SSE message received:', data);

      // Store our own client ID to ignore messages we sent
      if (data.type === 'connected') {
        sseClientIdRef.current = data.clientId;
        console.log(`Assigned SSE client ID: ${sseClientIdRef.current}`);
        return;
      }

      // Timings for a voice-over version changed state (queued / running /
      // done / failed). The audio studio and its editor listen for this; App
      // only relays it, it keeps no alignment state of its own.
      if (data.type === 'alignment_updated') {
        window.dispatchEvent(new CustomEvent('qv:alignment', { detail: data }));
        return;
      }

      // The stress glossary changed. It is a rule applied whenever a text is
      // read, so the audio studio re-reads its texts through it; relayed the
      // same way.
      if (data.type === 'voiceover_glossary_updated' || data.type === 'voiceover_replacements_updated') {
        window.dispatchEvent(new CustomEvent('qv:glossary', { detail: data }));
        return;
      }

      // Tasks: cards, chat messages, attempts and imports change in the
      // background (the Autoexecutor); the Tasks screen and its chips listen.
      if (typeof data.type === 'string' && data.type.startsWith('task_')) {
        window.dispatchEvent(new CustomEvent('qv:tasks', { detail: data }));
        return;
      }

      // Images were added server-side (a pipeline stage wrote into the images
      // table); the galleries refetch their list on this.
      if (data.type === 'quest_images_updated') {
        window.dispatchEvent(new CustomEvent('qv:images', { detail: data }));
        return;
      }

      // Handle location field updates
      if (data.type === 'location_field_updated') {
        setLocations(prevLocations => {
          if (!prevLocations) return [];
          const { locationUuid, field, value } = data;
          return prevLocations.map(loc => {
            if (loc.uuid === locationUuid) {
              return { ...loc, [field]: value };
            }
            return loc;
          });
        });
      }

      // Handle quest_info field updates
      if (data.type === 'quest_info_field_updated') {
        setQuestInfo(prevInfo => {
          if (!prevInfo) return null;
          const { field, value } = data;
          return {
            ...prevInfo,
            [field]: value,
          };
        });
      }

      // Handle full quest updates (for structural changes like add/remove/reorder)
      if (data.type === 'quest_fully_updated') {
        const questData = data.questData;
        normalizeLocationsInPlace(questData.locations);
        setQuestInfo(questData.quest_info || {});
        setLocations(questData.locations || []);
      }

      // --- Comment thread events (collaborative dialog) ---
      if (data.type === 'comment_added') {
        const { comment } = data;
        setCommentsByLocation(prev => {
          const list = prev[comment.location_uuid] || [];
          if (list.some(m => m.id === comment.id)) return prev; // dedupe
          return { ...prev, [comment.location_uuid]: [...list, comment] };
        });
      }
      if (data.type === 'comment_edited') {
        const { comment } = data;
        setCommentsByLocation(prev => {
          const list = prev[comment.location_uuid] || [];
          return { ...prev, [comment.location_uuid]: list.map(m => (m.id === comment.id ? comment : m)) };
        });
      }
      if (data.type === 'comment_deleted') {
        const { locationUuid, messageId } = data;
        setCommentsByLocation(prev => {
          const list = prev[locationUuid];
          if (!list) return prev;
          return { ...prev, [locationUuid]: list.filter(m => m.id !== messageId) };
        });
      }
    };

    eventSource.onerror = (err) => {
      // Never close here: transient drops auto-reconnect on their own (closing
      // by hand was what killed the channel until an F5). CLOSED means the
      // browser gave up permanently (HTTP error on reconnect) — rebuild fresh.
      console.error('SSE Error:', err);
      if (window.__qvStream !== 'poll') setStream('lost');
      if (eventSource.readyState === EventSource.CLOSED && !reconnectTimer) {
        reconnectTimer = setTimeout(() => setSseEpoch((n) => n + 1), 5000);
      }
    };

    // Cleanup on component unmount or when quest changes
    return () => {
      console.log(`🧹 Closing SSE for quest: ${selectedQuestId}`);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearInterval(watchdog);
      if (pollTimer) clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisible);
      eventSource.close();
      sseClientIdRef.current = null;
    };
  }, [selectedQuestId, isAuthenticated, selectedTable, sseEpoch]);

  // Handle successful login
  const handleLoginSuccess = (loggedInUsername, userFlags) => {
    setIsAuthenticated(true);
    setUsername(loggedInUsername);
    setCurrentUser({
      username: loggedInUsername,
      isAdmin: !!(userFlags && userFlags.isAdmin),
      canPushToProd: !!(userFlags && userFlags.canPushToProd),
    });
    if (userFlags && !userFlags.isAdmin) {
      setSelectedTable('quests_test');
    }
    setLoading(true); // Reload data after login
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setIsAuthenticated(false);
      setUsername(null);
      setCurrentUser(null);
      setShowAdminPanel(false);
      setQuestInfo(null);
      setLocations([]);
      setShowAudioStats(false); setShowAudioStudio(false); // Go back to default view on logout
      setShowTagsSummary(false); // Also hide tags summary
      // Clear last viewed quest and location from storage
      localStorage.removeItem('lastQuestId');
      localStorage.removeItem('lastLocationId');
      setSelectedQuestId('');
      setSelectedTable('quests_test'); // Reset to default on logout
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Start resize handling
  const startResize = (e) => {
    isDraggingRef.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', stopResize);
    document.body.style.userSelect = 'none'; // Prevent text selection during resize
    e.preventDefault(); // Prevent default behavior
  };

  // Handle mouse movement during resize
  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    
    const container = document.querySelector('.app-container');
    const isHorizontalLayout = window.innerWidth > 1024;
    
    if (isHorizontalLayout) {
      // Horizontal resize
      const containerWidth = container.offsetWidth;
      const newWidth = (e.clientX / containerWidth) * 100;
      
      // Limit the minimum and maximum width
      if (newWidth >= 10 && newWidth <= 95 && locationsListRef.current) {
        dragWidthRef.current = newWidth;
        locationsListRef.current.style.width = `${newWidth}%`;
      }
    } else {
      // Vertical resize
      const containerHeight = container.offsetHeight;
      const mapContainer = document.querySelector('.map-container');
      const resizer = document.querySelector('.resizer');
      
      // Calculate position of resizer from the top
      const resizerPos = e.clientY;
      const mapHeight = (resizerPos / containerHeight) * 100;
      
      // Apply limits
      if (mapHeight >= 20 && mapHeight <= 80) {
        // For vertical layout, we adjust the map height
        mapContainer.style.maxHeight = `${mapHeight}%`;
        
        // And adjust the locations list accordingly
        const locationsHeight = 100 - mapHeight - (resizer.offsetHeight / containerHeight * 100);
        locationsListRef.current.style.maxHeight = `${locationsHeight}%`;
      }
    }
  };

  // Handle touch movement for mobile devices
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling while resizing
    if (!isDraggingRef.current || !e.touches[0]) return;
    
    // Pass the touch position to the mouse move handler
    const touchEvent = {
      clientX: e.touches[0].clientX,
      clientY: e.touches[0].clientY
    };
    
    handleMouseMove(touchEvent);
  };

  // Stop resize handling
  const stopResize = () => {
    isDraggingRef.current = false;
    // Commit the dragged width once, so React's own style prop agrees with what
    // is already on the node and later renders keep it.
    if (dragWidthRef.current !== null) {
      setLeftPanelWidth(dragWidthRef.current);
      dragWidthRef.current = null;
    }
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', stopResize);
    document.body.style.userSelect = '';
  };

  // A render triggered by something else mid-drag (live pipeline events, save
  // status) would re-apply the last committed width and snap the divider back.
  // Re-assert the dragged width right after every such render.
  useLayoutEffect(() => {
    if (isDraggingRef.current && dragWidthRef.current !== null && locationsListRef.current) {
      locationsListRef.current.style.width = `${dragWidthRef.current}%`;
    }
  });

  // Save selected table to localStorage
  useEffect(() => {
    localStorage.setItem('selectedTable', selectedTable);
  }, [selectedTable]);

  // Save selected quest and location to localStorage
  useEffect(() => {
    if (selectedQuestId) {
      localStorage.setItem('lastQuestId', selectedQuestId);
    }
  }, [selectedQuestId]);

  // Keep the address bar in sync with the current environment + quest + point:
  // /<env>/<questId>?location=<uuid>[&view=<view>]. The URL is meaningful and
  // re-enterable. Switching env/quest/view pushes a history entry (so
  // back/forward move between them); merely changing the selected point —
  // including via scroll-spy — rewrites the current entry so it doesn't flood
  // history. The first sync
  // replaces the entry so the initial bare URL isn't left behind.
  const didInitUrlRef = useRef(false);
  const lastNavKeyRef = useRef(null);
  const currentView = showQuestInfoEditor ? VIEW_INFO
    : showAudioStats ? VIEW_AUDIO_STATS
    : showTagsSummary ? VIEW_TAGS
    : null;
  useEffect(() => {
    if (showPipeline || showPlatforms || showTasks || showProcesses) return; // pipeline/platforms/tasks/processes own the URL while open
    const selectedLoc = locations.find(loc => loc.id === selectedLocationId);
    const path = buildSelectionPath(selectedTable, selectedQuestId);
    // Use the selected point's uuid; before it resolves on a deep link, keep the
    // pending uuid from the URL so it isn't wiped out mid-load.
    const uuid = (showAudioStudio && studioState && studioState.locUuid)
      || (selectedLoc && selectedLoc.uuid) || pendingLocationUuid;
    const q = new URLSearchParams();
    if (uuid) q.set('location', uuid);
    if (currentView && !showAudioStudio) q.set('view', currentView);
    if (showAudioStudio && studioState) {
      q.set('studio', studioState.tab);
      q.set('field', studioState.field);
    }
    const search = q.toString() ? `?${q.toString()}` : '';
    const desired = path + search + window.location.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;

    const navKey = `${selectedTable}|${selectedQuestId}|${currentView || ''}`;
    if (current !== desired) {
      // New history entry only when the env/quest/view changes, not on point changes.
      if (didInitUrlRef.current && navKey !== lastNavKeyRef.current) {
        window.history.pushState({}, '', desired);
      } else {
        window.history.replaceState({}, '', desired);
      }
    }
    lastNavKeyRef.current = navKey;
    didInitUrlRef.current = true;
  }, [selectedTable, selectedQuestId, selectedLocationId, locations, pendingLocationUuid,
      showPipeline, showPlatforms, showTasks, showProcesses, showAudioStudio, studioState, currentView]);

  // The per-point task counts behind the chips (light: one list call).
  useEffect(() => {
    if (!selectedQuestId || !isAuthenticated) { setTaskCountsByLocation(null); return undefined; }
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`/api/tasks/quest/${encodeURIComponent(selectedQuestId)}`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        const m = {};
        (d.tasks || []).forEach((t) => {
          const open = !['done', 'dismissed'].includes(t.status);
          [...new Set((t.targets || []).map((g) => g.locationUuid).filter(Boolean))].forEach((u) => {
            if (!m[u]) m[u] = { open: 0, total: 0 };
            m[u].total += 1;
            if (open) m[u].open += 1;
          });
        });
        if (alive) setTaskCountsByLocation(m);
      } catch (e) { /* the chips are a convenience; the Tasks screen is the source */ }
    };
    load();
    const onEv = () => load();
    window.addEventListener('qv:tasks', onEv);
    return () => { alive = false; window.removeEventListener('qv:tasks', onEv); };
  }, [selectedQuestId, isAuthenticated]);

  // Address bar for the Tasks screen: /<env>/<questId>/tasks?… Entering the
  // screen or switching the quest pushes a history entry; changing the open
  // card or a filter replaces it.
  const lastTasksNavRef = useRef(null);
  useEffect(() => {
    if (!showTasks || !selectedQuestId) return;
    const desired = buildTasksPath(selectedTable, selectedQuestId, tasksState);
    const current = window.location.pathname + window.location.search;
    if (current === desired) return;
    const navKey = `${selectedTable}|${selectedQuestId}`;
    if (lastTasksNavRef.current === navKey) window.history.replaceState({}, '', desired);
    else window.history.pushState({}, '', desired);
    lastTasksNavRef.current = navKey;
  }, [showTasks, selectedTable, selectedQuestId, tasksState]);
  useEffect(() => { if (!showTasks) lastTasksNavRef.current = null; }, [showTasks]);

  // Address bar for the pipeline tab: /pipeline or /pipeline/<runId>.
  useEffect(() => {
    if (!showPipeline) return;
    const desired = '/pipeline' + (pipelineRunId ? `/${encodeURIComponent(pipelineRunId)}` : '');
    if (window.location.pathname === desired) return;
    const wasPipeline = window.location.pathname.split('/').filter(Boolean)[0] === 'pipeline';
    if (wasPipeline) window.history.replaceState({}, '', desired); // run change within pipeline
    else window.history.pushState({}, '', desired); // entering pipeline
  }, [showPipeline, pipelineRunId]);

  // Address bar for the platform-listings page: /platforms.
  useEffect(() => {
    if (!showPlatforms) return;
    if (window.location.pathname === '/platforms') return;
    window.history.pushState({}, '', '/platforms');
  }, [showPlatforms]);

  // Address bar for the background-processes page: /processes.
  useEffect(() => {
    if (!showProcesses) return;
    if (window.location.pathname === '/processes') return;
    window.history.pushState({}, '', '/processes');
  }, [showProcesses]);
  // Any other screen opening closes the processes page (the screens' own
  // handlers predate it and do not know its flag).
  const otherScreenOpen = showPlatforms || showPipeline || showTasks || showAdminPanel || showAudioStats || showAudioStudio || showTagsSummary || showDiffViewer || showQuestInfoEditor || showImageUpscaler || showPromptBuilder;
  useEffect(() => { if (otherScreenOpen) setShowProcesses(false); }, [otherScreenOpen]);

  // Addresses of the screens that have one, for buttons rendered as links so a
  // middle click opens them in a new tab (see LinkButton): the editor on the
  // current quest + point, the audio studio, the pipeline and platform listings.
  const currentLocUuid = (showAudioStudio && studioState && studioState.locUuid)
    || (locations.find(loc => loc.id === selectedLocationId) || {}).uuid
    || pendingLocationUuid;
  const buildEditorHref = (extra = {}) => {
    const q = new URLSearchParams();
    if (currentLocUuid) q.set('location', currentLocUuid);
    Object.entries(extra).forEach(([k, v]) => q.set(k, v));
    const search = q.toString();
    return buildSelectionPath(selectedTable, selectedQuestId) + (search ? `?${search}` : '');
  };
  const navHrefs = {
    editor: buildEditorHref(),
    info: selectedQuestId ? buildEditorHref({ view: VIEW_INFO }) : null,
    audioStats: buildEditorHref({ view: VIEW_AUDIO_STATS }),
    tags: buildEditorHref({ view: VIEW_TAGS }),
    // Each quest in the header picker is a link too: /<env>/<questId>.
    quest: (questId) => buildSelectionPath(selectedTable, questId),
    studio: selectedQuestId
      ? buildEditorHref({
        studio: (studioState && studioState.tab) || 'splice',
        field: (studioState && studioState.field) || 'description',
      })
      : null,
    pipeline: '/pipeline',
    platforms: '/platforms',
    processes: '/processes',
    tasks: selectedQuestId ? buildTasksPath(selectedTable, selectedQuestId, showTasks ? tasksState : {}) : null,
  };

  // React to browser back/forward by restoring the selection from the URL.
  useEffect(() => {
    const onPopState = () => {
      if (parsePlatformsPath()) {
        setShowPlatforms(true);
        setShowPipeline(false);
        return;
      }
      setShowPlatforms(false);
      const ts = parseTasksPath();
      if (ts) {
        const sel = parseLocationToSelection();
        if (sel.table) setSelectedTable(sel.table);
        setSelectedQuestId(sel.questId || '');
        setTasksState(ts);
        setShowTasks(true);
        setShowPipeline(false);
        return;
      }
      setShowTasks(false);
      const pp = parsePipelinePath();
      if (pp.pipeline) {
        setShowPipeline(true);
        setPipelineRunId(pp.runId);
        return;
      }
      setShowPipeline(false);
      const sel = parseLocationToSelection();
      if (sel.table) {
        setSelectedTable(sel.table);
      }
      setSelectedQuestId(sel.questId || '');
      setPendingLocationUuid(new URLSearchParams(window.location.search).get('location'));
      const view = parseViewFromUrl();
      setShowQuestInfoEditor(view === VIEW_INFO);
      setShowAudioStats(view === VIEW_AUDIO_STATS);
      setShowTagsSummary(view === VIEW_TAGS);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (selectedLocationId != null) {
      localStorage.setItem('lastLocationId', String(selectedLocationId));
    }
  }, [selectedLocationId]);

  // Fetch list of available quests on initial load or table change
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchQuestsList = async () => {
      try {
        const response = await fetch(`/api/quests?table=${selectedTable}`, {
          credentials: 'include' // Important for cookies
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            setIsAuthenticated(false);
            return;
          }
          throw new Error(`Failed to fetch quests list: ${response.status}`);
        }
        
        const quests = await response.json();
        setQuestsList(quests);
        
        // Set the first quest as selected by default if available and none is selected
        if (quests && quests.length > 0 && !selectedQuestId) {
          setSelectedQuestId(quests[0].quest_id);
        } else if (!selectedQuestId) {
          // If no quests, ensure we clear any old data
          setQuestInfo(null);
          setLocations([]);
          setLoading(false);
        }

      } catch (err) {
        console.error('Error fetching quests list:', err);
        setError('Failed to load available quests. Please try again later.');
      }
    };

    fetchQuestsList();
  }, [isAuthenticated, selectedTable]);

  // Fetch quest data when selectedQuestId changes
  useEffect(() => {
    if (!selectedQuestId || !isAuthenticated) return;
    
    // Prevent re-fetching if the effect was triggered by a location selection
    // within the same quest — UNLESS a pipeline ingest bumped pipelineRefresh,
    // in which case we must re-pull the just-written location content.
    if (prevQuestIdRef.current === selectedQuestId && prevPipelineRefreshRef.current === pipelineRefresh) {
      return;
    }
    prevPipelineRefreshRef.current = pipelineRefresh;

    setLoading(true);
    setError(null);
    
    const fetchQuestData = async () => {
      try {
        const response = await fetch(`/api/quest/${selectedQuestId}?table=${selectedTable}`, {
          credentials: 'include' // Important for cookies
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }
          throw new Error(`Failed to fetch quest data: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Ensure each location has UUID and a numeric id; keep array order as-is.
        // Imported route skeletons carry neither — and the whole editor keys
        // selection, scrolling and React lists off `id`, so a missing one makes
        // every point look "selected" and crashes deep-link handling.
        normalizeLocationsInPlace(data.locations);

        setQuestInfo(data.quest_info || {});
        setLocations(data.locations || []);
        
        // Set the first location as selected by default, only if a specific location is not already targeted for selection.
        if (data.locations && data.locations.length > 0 && !locationToSelect && !pendingLocationUuid) {
          setSelectedLocationId(data.locations[0].id);
        }
      } catch (err) {
        console.error('Error fetching quest data:', err);
        setError('Failed to load quest data. Please try again later.');
      } finally {
        setLoading(false);
        prevQuestIdRef.current = selectedQuestId;
      }
    };

    fetchQuestData();
  }, [selectedQuestId, isAuthenticated, selectedTable, pipelineRefresh]);

  // Detect whether the selected quest is a pipeline draft and load its
  // per-location generation meta (status/draft per uuid). null for normal quests.
  useEffect(() => {
    if (!selectedQuestId || !isAuthenticated) { setPipelineMeta(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/pipeline/quest/${selectedQuestId}`, { credentials: 'include' });
        if (!r.ok) { if (!cancelled) setPipelineMeta(null); return; }
        const data = await r.json();
        if (cancelled) return;
        if (data.pipeline) {
          const byUuid = {};
          (data.pipeline.points || []).forEach((p) => { if (p.uuid) byUuid[p.uuid] = p; });
          setPipelineMeta({ runId: data.pipeline.runId, narrative_summary: data.pipeline.narrative_summary, byUuid, batch: data.pipeline.batch || null, factsBatch: data.pipeline.facts_batch || null, voiceoverBatch: data.pipeline.voiceover_batch || null, answerBatch: data.pipeline.answer_variants_batch || null, yoficationBatch: data.pipeline.yofication_batch || null, glossaryBatch: data.pipeline.glossary_batch || null, hintsBatch: data.pipeline.hints_batch || null });
        } else {
          setPipelineMeta(null);
        }
      } catch (e) { if (!cancelled) setPipelineMeta(null); }
    })();
    return () => { cancelled = true; };
  }, [selectedQuestId, isAuthenticated, pipelineRefresh, pipelineMetaRefresh]);

  // Patch a SINGLE location's content into `locations` in place, without the
  // "Loading quest data…" blank + whole-quest refetch that bumping
  // pipelineRefresh triggers. Used when a pipeline generation finishes inside
  // the point dialog: on close we pull only that point's fresh content so the
  // editor updates silently instead of flashing the entire list.
  const patchPipelinePoint = useCallback(async (uuid) => {
    if (!uuid || !selectedQuestId || !isAuthenticated) return;
    try {
      const r = await fetch(
        `/api/quest/${selectedQuestId}/location/${uuid}?table=${selectedTable}`,
        { credentials: 'include' }
      );
      if (!r.ok) {
        // Fall back to the heavy refresh so the user still sees the new content.
        if (r.status !== 404) setPipelineRefresh((k) => k + 1);
        return;
      }
      const data = await r.json();
      if (!data || !data.location) return;
      // Keep the existing frontend id/uuid stable (selection & React keys ride
      // on id); swap only the content.
      setLocations((prev) => prev.map((l) => (
        l.uuid === uuid ? { ...data.location, id: l.id, uuid: l.uuid } : l
      )));
    } catch (e) {
      console.error('Failed to patch pipeline point in place:', e);
    }
  }, [selectedQuestId, selectedTable, isAuthenticated]);

  // Attach the pipeline to the current (hand-made) quest: one POST creates a
  // linked run whose task list mirrors the quest's locations. The city goes
  // into the task/writer briefs, so ask for a human-readable name (the slug
  // from the quest row is only the suggestion).
  const handleEnablePipeline = async () => {
    const questMeta = (questsList || []).find((q) => q.quest_id === selectedQuestId);
    const city = window.prompt(
      'City for the briefs, as a guide would say it (e.g. «Стамбул» — this exact word goes into the prompt text):',
      (questMeta && questMeta.city) || ''
    );
    if (city == null) return; // cancelled
    try {
      const r = await fetch(`/api/pipeline/quest/${selectedQuestId}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ city: city.trim() }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) throw new Error((data && data.error) || `HTTP ${r.status}`);
      setPipelineRefresh((k) => k + 1); // pipelineMeta reloads → controls appear
    } catch (e) {
      alert(`Failed to enable the pipeline: ${e.message}`);
    }
  };

  // Load the comment threads for the selected quest (separate from quest_data).
  useEffect(() => {
    if (!selectedQuestId || !isAuthenticated) {
      setCommentsByLocation({});
      return;
    }
    let cancelled = false;
    const fetchComments = async () => {
      try {
        const response = await fetch(`/api/quest/${selectedQuestId}/comments`, {
          credentials: 'include',
        });
        if (!response.ok) return;
        const rows = await response.json(); // ordered by created_at ASC
        if (cancelled) return;
        const map = {};
        rows.forEach(row => {
          (map[row.location_uuid] = map[row.location_uuid] || []).push(row);
        });
        setCommentsByLocation(map);
      } catch (err) {
        console.error('Error fetching comments:', err);
      }
    };
    fetchComments();
    return () => { cancelled = true; };
  }, [selectedQuestId, isAuthenticated]);

  // --- Comment thread mutations ---
  const handleAddComment = useCallback(async (locationUuid, body) => {
    const text = (body || '').trim();
    if (!text) return;
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}/location/${locationUuid}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body: text, clientId: sseClientIdRef.current }),
      });
      if (!response.ok) return;
      const comment = await response.json();
      setCommentsByLocation(prev => {
        const list = prev[locationUuid] || [];
        if (list.some(m => m.id === comment.id)) return prev;
        return { ...prev, [locationUuid]: [...list, comment] };
      });
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  }, [selectedQuestId]);

  const handleEditComment = useCallback(async (locationUuid, messageId, body) => {
    const text = (body || '').trim();
    if (!text) return;
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}/location/${locationUuid}/comment/${messageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body: text, clientId: sseClientIdRef.current }),
      });
      if (!response.ok) return;
      const comment = await response.json();
      setCommentsByLocation(prev => {
        const list = prev[locationUuid] || [];
        return { ...prev, [locationUuid]: list.map(m => (m.id === messageId ? comment : m)) };
      });
    } catch (err) {
      console.error('Error editing comment:', err);
    }
  }, [selectedQuestId]);

  const handleDeleteComment = useCallback(async (locationUuid, messageId) => {
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}/location/${locationUuid}/comment/${messageId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId: sseClientIdRef.current }),
      });
      if (!response.ok) return;
      setCommentsByLocation(prev => {
        const list = prev[locationUuid];
        if (!list) return prev;
        return { ...prev, [locationUuid]: list.filter(m => m.id !== messageId) };
      });
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  }, [selectedQuestId]);

  // Load editor logins once (for the tag recipient picker).
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/usernames', { credentials: 'include' });
        if (!response.ok) return;
        const list = await response.json();
        if (!cancelled) setUsernames(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error fetching usernames:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Log a tag change into the location's thread (author/time stamped server-side).
  const emitTagEvent = useCallback(async (locationUuid, action, tag, assignees = []) => {
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}/location/${locationUuid}/tag-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, tag, assignees, clientId: sseClientIdRef.current }),
      });
      if (!response.ok) return;
      const event = await response.json();
      setCommentsByLocation(prev => {
        const list = prev[locationUuid] || [];
        if (list.some(m => m.id === event.id)) return prev;
        return { ...prev, [locationUuid]: [...list, event] };
      });
    } catch (err) {
      console.error('Error emitting tag event:', err);
    }
  }, [selectedQuestId]);

  // Tag presence still lives in location.tags; these wrap a tags update with a
  // thread event so every change is logged with author + recipients + time.
  const handleAddLocationTag = useCallback((locationId, tag, assignees = []) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;
    const current = Array.isArray(location.tags) ? location.tags : [];
    if (!current.includes(tag)) {
      handleLocationUpdate(locationId, 'tags', [...current, tag]);
    }
    emitTagEvent(location.uuid, 'add', tag, assignees);
  }, [locations, emitTagEvent]);

  const handleRemoveLocationTag = useCallback((locationId, tag) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;
    const current = Array.isArray(location.tags) ? location.tags : [];
    handleLocationUpdate(locationId, 'tags', current.filter(t => t !== tag));
    emitTagEvent(location.uuid, 'remove', tag);
  }, [locations, emitTagEvent]);

  const handleReassignLocationTag = useCallback((locationId, tag, assignees = []) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;
    emitTagEvent(location.uuid, 'assign', tag, assignees);
  }, [locations, emitTagEvent]);

  // Current recipients per tag per location, reconstructed from the tag events
  // in each thread: { [locationUuid]: { [tag]: [assignee, ...] } }.
  const tagAssigneesByLocation = useMemo(() => {
    const result = {};
    Object.entries(commentsByLocation).forEach(([locUuid, list]) => {
      const map = {};
      list.forEach(m => {
        if (m.kind !== 'tag' || !m.meta || !m.meta.tag) return;
        if (m.meta.action === 'remove') {
          delete map[m.meta.tag];
        } else {
          map[m.meta.tag] = Array.isArray(m.meta.assignees) ? m.meta.assignees : [];
        }
      });
      result[locUuid] = map;
    });
    return result;
  }, [commentsByLocation]);

  // The tag chips the Lens offers, as { tag, count } — count = how many points
  // of the open quest carry it. The five standing tags are always listed, even
  // at zero, so the filter answers "has anyone marked anything yet?" instead of
  // hiding the question; custom tags appear once the quest actually uses them.
  const questTags = useMemo(() => {
    const counts = new Map();
    (locations || []).forEach(loc =>
      (loc.tags || []).forEach(t => counts.set(t, (counts.get(t) || 0) + 1)));
    const custom = Array.from(counts.keys()).filter(t => !PREDEFINED_TAGS.includes(t)).sort();
    return [...PREDEFINED_TAGS, ...custom].map(tag => ({ tag, count: counts.get(tag) || 0 }));
  }, [locations]);

  // Everyone addressed by at least one tag in the open quest — the recipient
  // choices in the Lens. "All" is a pseudo-recipient and the signed-in user
  // already has their own "Me" chip, so both are left out here.
  const questAssignees = useMemo(() => {
    const me = currentUser && currentUser.username;
    const all = new Set();
    Object.values(tagAssigneesByLocation).forEach(byTag => {
      Object.values(byTag || {}).forEach(list => {
        (list || []).forEach(u => { if (u !== ALL_RECIPIENTS && u !== me) all.add(u); });
      });
    });
    return Array.from(all).sort();
  }, [tagAssigneesByLocation, currentUser]);

  // Scroll-spy: highlight the location currently in view as the list scrolls.
  useEffect(() => {
    if (!locationsListRef.current || !locations || locations.length === 0) return;
    // The actual scroller is the inner .locations-list (the container around it
    // is overflow:hidden); the scroll event must be bound there, not on the ref.
    const scroller = locationsListRef.current.querySelector('.locations-list');
    if (!scroller) return;

    const handleScroll = () => {
      if (performance.now() < suppressSpyUntil.current) return;

      const view = scroller.getBoundingClientRect();
      let bestId = null;
      let maxVisible = 0;

      locations.forEach(loc => {
        const el = document.getElementById(`location-${loc.id}`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        // Visible height of this location within the scroller's viewport.
        const visible = Math.min(view.bottom, r.bottom) - Math.max(view.top, r.top);
        if (visible > maxVisible) {
          maxVisible = visible;
          bestId = loc.id;
        }
      });

      if (bestId != null && bestId !== selectedLocationId) {
        setSelectedLocationId(bestId);
      }
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [locations, selectedLocationId]);

  // Save quest data to server
  const saveQuestData = useCallback(async (force = false) => {
    if (selectedTable === 'quests' || !isAuthenticated || !questInfo || !selectedQuestId || (!hasUnsavedChanges && !force)) return;
    
    // Store the current scroll position before saving
    const scrollPosition = locationsListRef.current ? locationsListRef.current.scrollTop : 0;
    
    setSaveStatus('saving');

    const questDataToSave = {
      quest_info: questInfo,
      locations: locations,
    };
    
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}?table=${selectedTable}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ questData: questDataToSave, table: 'quests_test', clientId: sseClientIdRef.current }), // Always save to test table
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          setSaveStatus('error');
          return;
        }
        throw new Error(`Failed to save quest data: ${response.status}`);
      }
      
      setSaveStatus('success');
      setHasUnsavedChanges(false);
      
      // After 2 seconds, reset status to idle
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      
      statusTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
      
      // Restore scroll position after state updates
      setTimeout(() => {
        if (locationsListRef.current) {
          locationsListRef.current.scrollTop = scrollPosition;
        }
      }, 0);
      
    } catch (err) {
      console.error('Error saving quest data:', err);
      setSaveStatus('error');
      
      // After 5 seconds, reset status to idle if it's still error
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      
      statusTimeoutRef.current = setTimeout(() => {
        if (saveStatus === 'error') {
          setSaveStatus('idle');
        }
      }, 5000);
      
      // Still restore scroll position even on error
      setTimeout(() => {
        if (locationsListRef.current) {
          locationsListRef.current.scrollTop = scrollPosition;
        }
      }, 0);
    }
  }, [questInfo, locations, selectedQuestId, hasUnsavedChanges, saveStatus, isAuthenticated, selectedTable]);

  // One structural change of the quest (a point added, removed, reordered):
  // the server edits the stored JSON in place and announces the result to
  // every tab; nothing else of the quest travels. Throws on failure (the
  // caller keeps its state as it was).
  const questOp = useCallback(async (path, method, body) => {
    if (selectedTable === 'quests' || !isAuthenticated || !selectedQuestId) throw new Error('Not editable here');
    setSaveStatus('saving');
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}${path}`, {
        method, headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...(body || {}), clientId: sseClientIdRef.current }),
      });
      if (!response.ok) {
        if (response.status === 401) setIsAuthenticated(false);
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Failed: ${response.status}`);
      }
      setSaveStatus('success');
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      return await response.json();
    } catch (err) {
      console.error(`Quest change ${method} ${path} failed:`, err);
      setSaveStatus('error');
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 5000);
      window.alert(`The change was not saved: ${err.message}`);
      throw err;
    }
  }, [selectedQuestId, selectedTable, isAuthenticated]);

  // NEW: Granular save function for single field updates
  const saveFieldUpdate = useCallback(async (locationId, field, value) => {
    if (selectedTable === 'quests' || !isAuthenticated || !locations || !selectedQuestId) return;

    const location = locations.find(loc => loc.id === locationId);
    if (!location || !location.uuid) {
      console.error("Cannot save field update: Location or UUID not found.");
      return;
    }

    setSaveStatus('saving');

    try {
      const response = await fetch(`/api/quest/${selectedQuestId}/location-field`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          locationUuid: location.uuid,
          field: field,
          value: value,
          clientId: sseClientIdRef.current
        }),
      });

      if (!response.ok) {
        if (response.status === 401) setIsAuthenticated(false);
        throw new Error(`Failed to save field: ${response.status}`);
      }
      
      setSaveStatus('success');
      setHasUnsavedChanges(false); // Since we just saved the latest change

      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);

    } catch (err) {
      console.error('Error saving field update:', err);
      setSaveStatus('error');
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => {
        if (saveStatus === 'error') setSaveStatus('idle');
      }, 5000);
    }
  }, [locations, selectedQuestId, saveStatus, isAuthenticated, selectedTable]);

  // Handle explicit save button click
  const handleSaveClick = () => {
    saveQuestData(true);
  };

  // Handle location update
  const handleLocationUpdate = (locationId, field, value) => {
    // Don't update if there's no change
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;
    
    // Handle nested field updates (e.g., answer_variations[0])
    if (field.includes('[') && field.includes(']')) {
      const [arrayName, indexStr] = field.split('[');
      const index = parseInt(indexStr.replace(']', ''));
      
      // Create a new array with the updated value
      const updatedArray = [...location[arrayName]];
      updatedArray[index] = value;
      
      setLocations(prevLocations => {
        const updatedLocations = prevLocations.map(loc => {
          if (loc.id === locationId) {
            return {
              ...loc,
              [arrayName]: updatedArray
            };
          }
          return loc;
        });
        
        return updatedLocations;
      });
    } else if (field === 'answer_variations') {
      // Direct update to the answer_variations array
      setLocations(prevLocations => {
        const updatedLocations = prevLocations.map(loc => {
          if (loc.id === locationId) {
            return {
              ...loc,
              answer_variations: value
            };
          }
          return loc;
        });
        
        return updatedLocations;
      });
    } else {
      // Simple field update
      setLocations(prevLocations => {
        const updatedLocations = prevLocations.map(loc => {
          if (loc.id === locationId) {
            return {
              ...loc,
              [field]: value
            };
          }
          return loc;
        });
        
        return updatedLocations;
      });
    }
    
    // Every edit is one field of one point: an element of an array saves
    // the array (nothing else of the quest travels).
    if (!field.includes('[')) {
      saveFieldUpdate(locationId, field, value);
    } else {
      const [arrayName, indexStr] = field.split('[');
      const updatedArray = [...(location[arrayName] || [])];
      updatedArray[parseInt(indexStr.replace(']', ''), 10)] = value;
      saveFieldUpdate(locationId, arrayName, updatedArray);
    }
  };

  const handleLocationSelect = (locationId, scrollOpts) => {
    // Just update the selected location ID to highlight the marker
    setSelectedLocationId(locationId);

    // Suppress the scroll-spy for the duration of the programmatic scroll so it
    // doesn't fight the selection. A normal click is 250ms ease + ~400ms tail;
    // navigating into a freshly-mounted list holds the target until layout
    // settles (up to ~2.5s), so suppress for that whole window.
    const scrollWindow = scrollOpts && scrollOpts.holdUntilStable ? 2800 : 700;
    suppressSpyUntil.current = performance.now() + scrollWindow;

    // Find the location element and fast-scroll it to the top of the list
    const locationElement = document.getElementById(`location-${locationId}`);
    fastScrollToElement(locationElement, scrollOpts);
  };

  const handleQuestSelect = (questId, options = {}) => {
    const { isNavigation = false } = options;

    if (promoDirty && questId !== selectedQuestId) {
      if (!window.confirm('The Promo panel has unsaved changes that will be lost. Switch quest anyway?')) {
        return;
      }
    }

    // Check for unsaved changes before switching quests
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Do you want to save them before switching quests?')) {
        saveQuestData(true);
      }
    }
    
    // When changing quests manually (not via navigation link), clear the last known location
    if (!isNavigation) {
      setLocationToSelect(null);
      setPendingLocationUuid(null);
      localStorage.removeItem('lastLocationId');
    }
    
    // If we are on the stats page, switch back to the locations view
    if (showAudioStats) {
      setShowAudioStats(false); setShowAudioStudio(false);
    }
    if (showTagsSummary) {
      setShowTagsSummary(false);
    }

    console.log("Selecting quest ID:", questId);
    setSelectedQuestId(questId);
    
    // Manually trigger data fetch for the selected quest
    // DELETED: Manual fetch was here, causing race conditions.
  };

  const handleTableSelect = (tableName) => {
    if (tableName === selectedTable) return;

    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes that will be lost. Are you sure you want to switch environments?')) {
        setHasUnsavedChanges(false); // Discard changes
      } else {
        return; // User cancelled
      }
    }

    setSelectedTable(tableName);
    // Reload quests
    setSelectedQuestId('');
    setQuestInfo(null);
    setLocations([]);
    setLoading(true);
  };

  // City of the open quest: the default for new/imported quests and the map.
  const currentQuestCity = (questsList.find(q => q.quest_id === selectedQuestId) || {}).city || null;

  // Open the "new quest" modal (the menu item). The id is set by hand there.
  const handleCreateQuest = () => setShowNewQuestModal(true);

  // Create a brand new (empty) quest with a hand-set id. New quests always land
  // in the test environment; we switch there, add the quest to the list and
  // select it so the user can start editing immediately. Throws on failure so
  // the modal can surface the error and stay open.
  const handleSubmitNewQuest = async ({ questName, questId, city, country }) => {
    const response = await fetch('/api/quest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ questName, questId, city, country }),
    });

    if (!response.ok) {
      let message = `Failed to create quest (${response.status})`;
      try {
        const body = await response.json();
        if (body && body.error) message = body.error;
      } catch (e) { /* ignore */ }
      throw new Error(message);
    }

    const created = await response.json();

    // New quests live in the test environment — switch there if needed.
    if (selectedTable !== 'quests_test') {
      setSelectedTable('quests_test');
    }

    // Make the new quest visible in the dropdown and select it.
    setQuestsList(prev => {
      const rest = prev.filter(q => q.quest_id !== created.quest_id);
      return [
        { quest_id: created.quest_id, city: created.city, country: created.country, iso_code: created.iso_code, quest_name: created.quest_name },
        ...rest,
      ];
    });
    handleQuestSelect(created.quest_id);
    setShowNewQuestModal(false);
  };

  // Open the "import quest" modal (the menu item).
  const handleImportQuest = () => setShowImportQuestModal(true);

  // Import a quest from a JSON file. The importer supplies id, city and country;
  // the JSON carries the route (quest_info + locations). Landing, list-refresh
  // and selection mirror the empty-create flow above. Throws on failure so the
  // modal can surface the error and stay open.
  const handleSubmitImportQuest = async ({ questId, city, country, questName, questData }) => {
    const response = await fetch('/api/quest/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ questId, city, country, questName, questData }),
    });

    if (!response.ok) {
      let message = `Failed to import quest (${response.status})`;
      try {
        const body = await response.json();
        if (body && body.error) message = body.error;
      } catch (e) { /* ignore */ }
      throw new Error(message);
    }

    const created = await response.json();

    // Imported quests live in the test environment — switch there if needed.
    if (selectedTable !== 'quests_test') {
      setSelectedTable('quests_test');
    }

    setQuestsList(prev => {
      const rest = prev.filter(q => q.quest_id !== created.quest_id);
      return [
        { quest_id: created.quest_id, city: created.city, country: created.country, iso_code: created.iso_code, quest_name: created.quest_name },
        ...rest,
      ];
    });
    handleQuestSelect(created.quest_id);
    setShowImportQuestModal(false);
  };

  // Append imported points to the currently open quest. The server broadcasts a
  // quest_fully_updated SSE, so the open editor picks up the new route on its
  // own; we just close the modal. Throws on failure so the modal can show it.
  const handleAppendLocations = async ({ questId, locations }) => {
    const response = await fetch(`/api/quest/${encodeURIComponent(questId)}/append-locations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ locations, clientId: sseClientIdRef.current }),
    });

    if (!response.ok) {
      let message = `Failed to add points (${response.status})`;
      try {
        const body = await response.json();
        if (body && body.error) message = body.error;
      } catch (e) { /* ignore */ }
      throw new Error(message);
    }

    setShowImportQuestModal(false);
  };

  // Set up auto-save on changes
  useEffect(() => {
    if (!hasUnsavedChanges || !isAuthenticated) return;
    
    // Save to the server immediately when changes are made
    saveQuestData(true);
    
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, saveQuestData, isAuthenticated]);

  // Effect to handle selecting a location after quest data has loaded
  useEffect(() => {
    if (locationToSelect && locations && !loading && !showAudioStats && !showTagsSummary && !showAudioStudio && !showQuestInfoEditor) {
      const locationExists = locations.some(loc => loc.id === locationToSelect);
      if (locationExists) {
        // The list just remounted (we came from another view), so rows above
        // the target keep growing as they lay out. Start the scroll next frame
        // and hold the target pinned until the layout settles, instead of
        // racing a fixed timeout — see fastScrollToElement(holdUntilStable).
        const id = locationToSelect;
        setLocationToSelect(null);
        requestAnimationFrame(() =>
          handleLocationSelect(id, { holdUntilStable: true })
        );
      }
    }
  }, [locations, loading, locationToSelect, showAudioStats, showTagsSummary, showAudioStudio, showQuestInfoEditor]);

  // Resolve a location uuid from the URL (?location=) to a point, once the
  // quest's locations are available.
  useEffect(() => {
    if (!pendingLocationUuid || loading || showAudioStats || showTagsSummary || showAudioStudio || showQuestInfoEditor) return;
    if (!locations || locations.length === 0) return;
    const match = locations.find(loc => loc.uuid === pendingLocationUuid);
    if (match) {
      const id = match.id;
      requestAnimationFrame(() =>
        handleLocationSelect(id, { holdUntilStable: true })
      );
    } else {
      // Unknown uuid in the URL — fall back to the first point.
      setSelectedLocationId(locations[0].id);
    }
    setPendingLocationUuid(null);
  }, [locations, loading, pendingLocationUuid, showAudioStats, showTagsSummary, showAudioStudio, showQuestInfoEditor]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  // Handle location reordering: the new order is shown at once and sent as
  // the list of uuids; a refusal (the points changed elsewhere) puts the old
  // order back.
  const handleLocationReorder = async (locationId, direction, targetPosition = null) => {
    if (!locations) return;
    const prevLocations = locations;
    const newLocations = [...prevLocations];
    const currentIndex = newLocations.findIndex(loc => loc.id === locationId);
    if (currentIndex === -1) return;
    if (direction === 'position' && targetPosition !== null) {
      const [movedLocation] = newLocations.splice(currentIndex, 1);
      newLocations.splice(targetPosition, 0, movedLocation);
    } else {
      let targetIndex;
      if (direction === 'up') {
        targetIndex = currentIndex - 1;
        if (targetIndex < 0) return;
      } else if (direction === 'down') {
        targetIndex = currentIndex + 1;
        if (targetIndex >= newLocations.length) return;
      } else {
        return;
      }
      [newLocations[currentIndex], newLocations[targetIndex]] = [newLocations[targetIndex], newLocations[currentIndex]];
    }
    setLocations(newLocations);
    try {
      await questOp('/locations/order', 'PUT', { uuids: newLocations.map(loc => loc.uuid) });
    } catch (e) {
      setLocations(prevLocations);
    }
  };

  // Add a new location
  const handleAddLocation = async () => {
      const newLocations = [...(locations || [])];
      
      const nextId = newLocations.length > 0 
        ? Math.max(...newLocations.map(loc => loc.id)) + 1 
        : 1;
      
      // Генерируем UUID для новой локации
      const newUuid = uuidv4();
      
      // Create a default position (near the center of existing locations or default)
      let newCoordinates;
      
      // Average the position of existing points that actually have coordinates
      // (an imported route skeleton may not have any yet).
      const placed = newLocations.filter(loc =>
        Array.isArray(loc.coordinates) && loc.coordinates.length >= 2 &&
        Number.isFinite(Number(loc.coordinates[0])) && Number.isFinite(Number(loc.coordinates[1]))
      );
      if (placed.length > 0) {
        const sumLat = placed.reduce((sum, loc) => sum + Number(loc.coordinates[0]), 0);
        const sumLng = placed.reduce((sum, loc) => sum + Number(loc.coordinates[1]), 0);
        const avgLat = sumLat / placed.length;
        const avgLng = sumLng / placed.length;

        // Add a small random offset to avoid exact overlay
        const offsetLat = (Math.random() - 0.5) * 0.01;
        const offsetLng = (Math.random() - 0.5) * 0.01;

        newCoordinates = [avgLat + offsetLat, avgLng + offsetLng];
      } else {
        // Default coordinates (Istanbul center)
        newCoordinates = [41.0082, 28.9784];
      }
      
      // Create a new location with default values
      const newLocation = {
        id: nextId,
        uuid: newUuid,
        name: `Location ${newLocations.length + 1}`,
        comment: '',
        description: 'New location description',
        task: 'New location task',
        coordinates: newCoordinates,
        answer_variations: ['Default answer'],
        hint: '',
        after_message: 'You completed this location!',
        type: 'ordinary',
        tags: [],
        branching_message: '',
        branches: [],
      };
      
      // Stored first (one point appended to the quest), shown once the server has it.
      try {
        await questOp('/locations', 'POST', { location: newLocation });
      } catch (e) {
        return;
      }
      setLocations(prev => [...(prev || []), newLocation]);
      setSelectedLocationId(nextId);
  };

  // Handle updating a location's coordinates
  const handleLocationMove = (locationId, newCoordinates) => {
    setLocations(prevLocations => prevLocations.map(loc => (loc.id === locationId ? { ...loc, coordinates: newCoordinates } : loc)));
    // The coordinates are one field of one point, saved like any other field.
    saveFieldUpdate(locationId, 'coordinates', newCoordinates);
  };

  const handleMoveLocationToQuest = useCallback(
    async (locationUuid, toQuestId) => {
      if (!selectedQuestId || selectedTable === 'quests' || !isAuthenticated) return;
      if (toQuestId === selectedQuestId) return;

      if (hasUnsavedChanges) {
        const ok = window.confirm(
          'You have unsaved changes. Save them before moving this location?'
        );
        if (!ok) return;
        await saveQuestData(true);
      }

      setSaveStatus('saving');
      try {
        const response = await fetch(`/api/quest/${selectedQuestId}/move-location`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            locationUuid,
            toQuestId,
            clientId: sseClientIdRef.current,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) setIsAuthenticated(false);
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `Move failed (${response.status})`);
        }

        const data = await response.json();
        const toData = data.toQuestData;
        normalizeLocationsInPlace(toData.locations);
        prevQuestIdRef.current = data.toQuestId;
        setQuestInfo(toData.quest_info || {});
        setLocations(toData.locations || []);
        setSelectedQuestId(data.toQuestId);
        setSelectedLocationId(data.newLocationId);
        setHasUnsavedChanges(false);
        setLoading(false);
        setSaveStatus('success');
        if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Move location error:', err);
        alert(err.message || 'Failed to move location');
        setSaveStatus('error');
        if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 5000);
      }
    },
    [
      selectedQuestId,
      selectedTable,
      isAuthenticated,
      hasUnsavedChanges,
      saveQuestData,
    ]
  );

  // Handle removing a location
  const handleRemoveLocation = async (locationId) => {
    const target = (locations || []).find(loc => loc.id === locationId);
    if (!target || !target.uuid) return;
    try {
      await questOp(`/location/${target.uuid}`, 'DELETE', {});
    } catch (e) {
      return;
    }
    setLocations(prevLocations => {
      // Filter out the location with the given ID
      const updatedLocations = prevLocations.filter(loc => loc.id !== locationId);
      
      // If we're removing the currently selected location, select another one
      if (locationId === selectedLocationId && updatedLocations.length > 0) {
        // Find the next location ID or use the first one
        const locationIds = updatedLocations.map(loc => loc.id);
        const nextLocationId = Math.min(...locationIds.filter(id => id > locationId)) || 
                              Math.max(...locationIds.filter(id => id < locationId)) ||
                              locationIds[0];
        
        setSelectedLocationId(nextLocationId);
      } else if (updatedLocations.length === 0) {
        // If no locations left, clear selection
        setSelectedLocationId(null);
      }
      return updatedLocations;
    });
  };

  const handleEditQuestInfo = () => {
    setShowQuestInfoEditor(true);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };
  
  const handleQuestInfoEditorBack = () => {
    setShowQuestInfoEditor(false);
  };
  
  const handleEditLocations = () => {
    setShowQuestInfoEditor(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  const handleShowAudioStats = () => {
    setShowAudioStats(true);
    setShowQuestInfoEditor(false);
    setShowTagsSummary(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  const handleShowAudioStudio = () => {
    setShowAudioStudio(true);
    setShowAudioStats(false);
    setShowTagsSummary(false);
    setShowQuestInfoEditor(false);
    setShowDiffViewer(false);
  };

  const handleShowTagsSummary = () => {
    setShowTagsSummary(true);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowQuestInfoEditor(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  const handleShowDiffViewer = () => {
    setShowDiffViewer(true);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowQuestInfoEditor(false);
    setShowTagsSummary(false);
    setShowAdminPanel(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  const handleShowAdminPanel = () => {
    setShowTasks(false);
    setShowPlatforms(false);
    setShowAdminPanel(true);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowQuestInfoEditor(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  const handleShowPipeline = () => {
    setShowTasks(false);
    setShowPlatforms(false);
    setShowPipeline(true);
    setShowTasks(false);
    setShowAdminPanel(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowQuestInfoEditor(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  const handleShowTasks = (st) => {
    setShowTasks(true);
    setTasksState(st && typeof st === 'object' ? st : {});
    setShowPlatforms(false);
    setShowPipeline(false);
    setShowAdminPanel(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowQuestInfoEditor(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };
  const handleCloseTasks = () => {
    setShowTasks(false);
    window.history.pushState({}, '', buildSelectionPath(selectedTable, selectedQuestId));
  };

  const handleShowPlatformListings = () => {
    setShowTasks(false);
    setShowPlatforms(true);
    setShowPipeline(false);
    setShowAdminPanel(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowQuestInfoEditor(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
  };

  // Leaving the platform-listings page puts the address back on the current
  // quest — otherwise /platforms would stay in the bar above the editor.
  const handleClosePlatformListings = () => {
    setShowPlatforms(false);
    window.history.pushState({}, '', buildSelectionPath(selectedTable, selectedQuestId));
  };

  const handleShowProcesses = () => {
    setShowTasks(false);
    setShowPlatforms(false);
    setShowPipeline(false);
    setShowAdminPanel(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowQuestInfoEditor(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);
    setShowProcesses(true);
  };
  const handleCloseProcesses = () => {
    setShowProcesses(false);
    window.history.pushState({}, '', buildSelectionPath(selectedTable, selectedQuestId));
  };

  const handleShowImageUpscaler = () => {
    setShowTasks(false);
    setShowPlatforms(false);
    setShowImageUpscaler(true);
    setShowPromptBuilder(false);
    setShowAdminPanel(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowQuestInfoEditor(false);
  };

  const handleShowPromptBuilder = () => {
    setShowTasks(false);
    setShowPlatforms(false);
    setShowPromptBuilder(true);
    setShowImageUpscaler(false);
    setShowAdminPanel(false);
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowTagsSummary(false);
    setShowDiffViewer(false);
    setShowQuestInfoEditor(false);
  };

  const handleCloseAdminPanel = () => {
    setShowAdminPanel(false);
  };

  const handleCloseDiffViewer = () => {
    setShowDiffViewer(false);
  };

  const showSpeechGenerationView = (locationId, field) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      const voiceoverField = `${field}_voiceover`;
      const initialText = location[voiceoverField] || location[field] || '';

      const orderIndex = locations.findIndex(l => l.id === locationId);
      setSpeechGenInitialData({
        text: initialText,
        locationId: locationId,
        locationUuid: location.uuid,
        field: field,
        locationName: location.name,
        locationOrder: orderIndex >= 0 ? orderIndex + 1 : null,
        questId: selectedQuestId,
      });
      setRightPanelView('speech');
      // Also ensure other full-screen views are hidden
      setShowAudioStats(false); setShowAudioStudio(false);
      setShowQuestInfoEditor(false);
      setShowTagsSummary(false);
    }
  };

  const showMapView = () => {
    setRightPanelView('map');
  };

  const handleSaveVoiceoverText = (locationId, field, text) => {
    const voiceoverField = `${field}_voiceover`;
    handleLocationUpdate(locationId, voiceoverField, text);
  };

  const handleNavigateToLocation = (questId, locationId) => {
    // Switch back to the editor view
    setShowAudioStats(false); setShowAudioStudio(false);
    setShowQuestInfoEditor(false);
    setShowTagsSummary(false);
    setShowImageUpscaler(false);
    setShowPromptBuilder(false);

    // If we're already on the right quest, we still use setLocationToSelect
    // to ensure the DOM is updated before we try to scroll.
    if (questId === selectedQuestId) {
      setLocationToSelect(locationId);
    } else {
      // If we need to switch quests, store the location to select
      // and then switch the quest.
      setLocationToSelect(locationId);
      handleQuestSelect(questId, { isNavigation: true });
    }
  };

  const handleOpenCopyModal = () => setShowCopyModal(true);
  const handleCloseCopyModal = () => setShowCopyModal(false);

  const handleExportMarkdown = () => setMarkdownExport({ mode: 'quest' });
  const handleExportLocationMarkdown = (location, index) =>
    setMarkdownExport({ mode: 'location', location, index });

  // Build a printable PDF of the current quest (text + field images) and open
  // the browser's print-to-PDF dialog.
  const handleExportPdf = async () => {
    if (!questInfo || !selectedQuestId) {
      console.warn('No quest data available to export');
      return;
    }
    try {
      await exportQuestToPdf({
        questInfo,
        locations,
        questId: selectedQuestId,
        table: selectedTable,
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Could not export PDF. See console for details.');
    }
  };

  // Build a filtered quest JSON string from the selected fields
  const buildQuestJsonString = (selectedFields) => {
    // Build quest_info in the provided visual order
    const filteredQuestInfo = {};
    const questInfoOrder = selectedFields.questInfoOrder || Object.keys(questInfo);
    questInfoOrder.forEach((key) => {
      if (selectedFields.questInfoFields[key] && key in questInfo) {
        filteredQuestInfo[key] = questInfo[key];
      }
    });

    // Build locations with keys in the provided visual order
    const locationOrder = selectedFields.locationOrder;
    const filteredLocations = locations.map((location) => {
      const filteredLocation = {};
      const keysToUse = locationOrder || Object.keys(location);
      keysToUse.forEach((key) => {
        if (selectedFields.locationFields[key] && key in location) {
          filteredLocation[key] = location[key];
        }
      });
      return filteredLocation;
    });

    const questData = {
      quest_info: filteredQuestInfo,
      locations: filteredLocations,
    };

    return JSON.stringify(questData, null, 2);
  };

  // Save full quest JSON as a downloadable file named "<questId>_<YYYY-MM-DD_HH-MM-SS>.json"
  const handleSaveQuestJsonFile = (selectedFields) => {
    if (!questInfo || !locations || !selectedQuestId) {
      console.warn('No quest data available to save');
      return;
    }

    const jsonString = buildQuestJsonString(selectedFields);

    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    const fileName = `${selectedQuestId}_${stamp}.json`;

    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Copy full quest JSON to clipboard
  const handleCopyQuestJson = async (selectedFields) => {
    if (!questInfo || !locations || !selectedQuestId) {
      console.warn('No quest data available to copy');
      return;
    }

    const jsonString = buildQuestJsonString(selectedFields);

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(jsonString);
        console.log('Quest JSON copied to clipboard');
      } else {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = jsonString;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            console.log('Quest JSON copied to clipboard');
          } else {
            throw new Error('Copy command failed');
          }
        } catch (err) {
          console.error('Failed to copy quest JSON:', err);
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy quest JSON:', err);
    }
  };

  const handleQuestInfoUpdate = (field, value) => {
    setQuestInfo(prevInfo => {
      const newInfo = {
        ...(prevInfo || {}),
        [field]: value
      };
      
      // If updating quest_name, also update quest_info.quest_name
      if (field === 'quest_name') {
        newInfo.quest_name = value;
      }
      
      return newInfo;
    });
    // Immediately save the specific field
    saveQuestInfoField(field, value);
  };

  const saveQuestInfoField = useCallback(async (field, value) => {
    if (selectedTable === 'quests' || !isAuthenticated || !selectedQuestId) return;
    
    setSaveStatus('saving');
    try {
      const response = await fetch(`/api/quest/${selectedQuestId}/quest-info-field`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ field, value, clientId: sseClientIdRef.current }),
      });

      if (!response.ok) {
        if (response.status === 401) setIsAuthenticated(false);
        throw new Error(`Failed to save quest info field: ${response.status}`);
      }

      setSaveStatus('success');
      setHasUnsavedChanges(false);

      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);

    } catch (err) {
      console.error('Error saving quest info field:', err);
      setSaveStatus('error');
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => {
        if (saveStatus === 'error') setSaveStatus('idle');
      }, 5000);
    }
  }, [selectedQuestId, saveStatus, isAuthenticated, selectedTable]);

  // If still checking authentication status, show loading
  if (checkingAuth) {
    return <div className="loading">Loading authentication...</div>;
  }

  // If not authenticated, show the login page
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Define read-only mode based on selected table
  const isReadOnly = selectedTable === 'quests';

  // Debug log - log what the questData looks like for debugging
  console.log("Current questInfo:", questInfo);
  console.log("Current locations:", locations);
  console.log("Loading state:", loading);
  console.log("Error state:", error);
  console.log("Selected Quest ID:", selectedQuestId);

  return (
    <div className="app-container">
      <div className="quest-selector-wrapper fixed">
        <QuestSelector
          quests={questsList}
          onQuestSelect={handleQuestSelect}
          selectedQuestId={selectedQuestId}
          selectedTable={selectedTable}
          onTableSelect={handleTableSelect}
          isReadOnly={isReadOnly}
          saveStatus={saveStatus}
          onLogout={handleLogout}
          onEditQuestInfo={handleEditQuestInfo}
          onEditLocations={handleEditLocations}
          onShowAudioStats={handleShowAudioStats}
          onShowTagsSummary={handleShowTagsSummary}
          onShowAudioStudio={handleShowAudioStudio}
          onShowDiffViewer={handleShowDiffViewer}
          onCopyQuestJson={handleOpenCopyModal}
          onExportPdf={handleExportPdf}
          onExportMarkdown={handleExportMarkdown}
          currentUser={currentUser}
          onShowAdminPanel={handleShowAdminPanel}
          onCreateQuest={handleCreateQuest}
          onImportQuest={handleImportQuest}
          onShowPipeline={handleShowPipeline}
          onShowPlatformListings={handleShowPlatformListings}
          onShowProcesses={handleShowProcesses}
          onShowTasks={handleShowTasks}
          onShowImageUpscaler={handleShowImageUpscaler}
          onShowPromptBuilder={handleShowPromptBuilder}
          navHrefs={navHrefs}
          pipelineMode={showPipeline}
          lens={lens}
          onLensChange={handleLensChange}
          availableTags={questTags}
          availableAssignees={questAssignees}
        />
      </div>
      
      {showCopyModal && (
        <CopyQuestJsonModal
          questInfo={questInfo}
          locations={locations}
          onClose={handleCloseCopyModal}
          onCopy={handleCopyQuestJson}
          onSaveFile={handleSaveQuestJsonFile}
        />
      )}

      {markdownExport && (
        <ExportMarkdownModal
          mode={markdownExport.mode}
          questInfo={questInfo}
          locations={locations}
          location={markdownExport.location}
          index={markdownExport.index}
          fileBaseName={markdownExport.mode === 'location'
            ? (markdownExport.location && markdownExport.location.name) || selectedQuestId
            : selectedQuestId}
          onClose={() => setMarkdownExport(null)}
        />
      )}

      {showNewQuestModal && (
        <NewQuestModal
          onClose={() => setShowNewQuestModal(false)}
          onCreate={handleSubmitNewQuest}
          defaultCity={currentQuestCity || 'istanbul'}
        />
      )}

      {showImportQuestModal && (
        <ImportQuestModal
          onClose={() => setShowImportQuestModal(false)}
          onImport={handleSubmitImportQuest}
          onAppend={handleAppendLocations}
          currentQuest={selectedQuestId ? {
            id: selectedQuestId,
            name: (questInfo && questInfo.quest_name) || selectedQuestId,
            city: currentQuestCity || '',
          } : null}
        />
      )}

      {showTasks && selectedQuestId ? (
        <TasksScreen
          questId={selectedQuestId}
          questName={(questInfo && questInfo.quest_name) || selectedQuestId}
          locations={locations}
          currentUser={currentUser && currentUser.username}
          state={tasksState}
          onStateChange={setTasksState}
          backHref={navHrefs.editor}
          onBack={handleCloseTasks}
          hrefs={{
            editor: (uuid) => `${buildSelectionPath(selectedTable, selectedQuestId)}?location=${uuid}`,
            studio: (uuid, field, versionId) => `${buildSelectionPath(selectedTable, selectedQuestId)}?location=${uuid}&studio=splice&field=${field || 'description'}${versionId ? `&version=${versionId}` : ''}`,
          }}
        />
      ) : showProcesses ? (
        <BgProcesses onClose={handleCloseProcesses} closeHref={navHrefs.editor} />
      ) : showPlatforms ? (
        <PlatformListings onClose={handleClosePlatformListings} closeHref={navHrefs.editor} />
      ) : showPipeline ? (
        <PipelinePanel
          initialRunId={pipelineRunId}
          onRunChange={setPipelineRunId}
          onClose={() => { setShowPipeline(false); setPipelineRunId(null); }}
          closeHref={navHrefs.editor}
          onOpenQuest={(qid, meta) => {
            setShowPipeline(false);
            setPipelineRunId(null);
            // The draft lives in quests_test; make sure we're there and that it's
            // in the quest menu.
            if (selectedTable !== 'quests_test') setSelectedTable('quests_test');
            if (meta) {
              setQuestsList(prev => {
                const rest = prev.filter(q => q.quest_id !== qid);
                return [{ quest_id: qid, city: meta.city, quest_name: meta.quest_name }, ...rest];
              });
            }
            handleQuestSelect(qid);
          }}
        />
      ) : showAdminPanel ? (
        <AdminPanel onClose={handleCloseAdminPanel} backHref={navHrefs.editor} />
      ) : showImageUpscaler ? (
        <ImageUpscaler onBack={handleEditLocations} backHref={navHrefs.editor} />
      ) : showPromptBuilder ? (
        <PromptBuilder onBack={handleEditLocations} backHref={navHrefs.editor} />
      ) : loading ? (
        <div className="loading">Loading quest data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : showDiffViewer ? (
        <QuestDiffViewer
          questId={selectedQuestId}
          onClose={handleCloseDiffViewer}
          backHref={navHrefs.editor}
          canPushToProd={!!(currentUser && currentUser.canPushToProd)}
        />
      ) : showAudioStats ? (
        <AudioStats onBack={handleEditLocations} backHref={navHrefs.editor} onNavigateToLocation={handleNavigateToLocation} />
      ) : showAudioStudio ? (
        <AudioStudio
          questId={selectedQuestId}
          questName={(questInfo && questInfo.quest_name) || selectedQuestId}
          locations={locations}
          taskCountsByLocation={taskCountsByLocation}
          tasksHref={(uuid) => buildTasksPath(selectedTable, selectedQuestId, { point: uuid })}
          // On a reload straight into the studio the uuid from the URL is never
          // resolved into a selected point (that effect stands down while the
          // studio is open), so the studio takes the URL's uuid as it is.
          initialLocationUuid={pendingLocationUuid || (locations.find(l => l.id === selectedLocationId) || {}).uuid}
          onBack={handleEditLocations}
          backHref={navHrefs.editor}
          onNavigateToLocation={handleNavigateToLocation}
          onSaveVoiceoverText={handleSaveVoiceoverText}
          initialField={studioEntry && studioEntry.field}
          initialTab={studioEntry && studioEntry.tab}
          initialVersionId={studioEntry && studioEntry.version}
          initialDraft={studioEntry && studioEntry.draft}
          onStateChange={setStudioState}
        />
      ) : showTagsSummary ? (
        <TagsSummary onBack={handleEditLocations} backHref={navHrefs.editor} onNavigateToLocation={handleNavigateToLocation} currentQuestId={selectedQuestId} currentUsername={currentUser && currentUser.username} />
      ) : !questInfo ? (
        <div className="error">No quest data available</div>
      ) : (
        <ErrorBoundary resetKey={selectedQuestId}>
        <div className="app-content">
          <div 
            ref={locationsListRef} 
            className="locations-list-container"
            style={{ 
              width: isNarrowLayout ? '100%' : `${leftPanelWidth}%`,
              flex: isNarrowLayout ? '1 1 auto' : 'none'
            }}
          >
            {showQuestInfoEditor ? (
              <div className="quest-info-scroll">
                {/* One visual form: header + meta summary on top, the quest_info
                    fields, then Price (test and prod) and Promo (straight-to-prod) below. */}
                <QuestInfoEditor
                  questInfo={questInfo}
                  questId={selectedQuestId}
                  selectedTable={selectedTable}
                  locations={locations}
                  onQuestInfoUpdate={handleQuestInfoUpdate}
                  isReadOnly={isReadOnly}
                />
                <PriceEditor
                  questId={selectedQuestId}
                  selectedTable={selectedTable}
                  onPreviewChange={setPreviewPrice}
                />
                <OpensAtEditor
                  questId={selectedQuestId}
                  selectedTable={selectedTable}
                  onPreviewChange={setPreviewOpensAt}
                />
                <PromoEditor
                  questId={selectedQuestId}
                  canPushToProd={!!(currentUser && currentUser.canPushToProd)}
                  selectedTable={selectedTable}
                  questInfoUuid={questInfo && questInfo.uuid}
                  isReadOnly={isReadOnly}
                  onPreviewChange={setPreviewPromo}
                  onDirtyChange={setPromoDirty}
                />
              </div>
            ) : (
              <>
              {pipelineMeta ? (
                <>
                <BatchPanels
                  questId={selectedQuestId}
                  runId={pipelineMeta.runId}
                  locations={locations}
                  pointsByUuid={pipelineMeta.byUuid}
                  initialBatch={pipelineMeta.batch}
                  initialFactsBatch={pipelineMeta.factsBatch}
                  initialVoiceoverBatch={pipelineMeta.voiceoverBatch}
                  initialAnswerBatch={pipelineMeta.answerBatch}
                  initialYoficationBatch={pipelineMeta.yoficationBatch}
                  initialGlossaryBatch={pipelineMeta.glossaryBatch}
                  initialHintsBatch={pipelineMeta.hintsBatch}
                  onChange={() => setPipelineRefresh((k) => k + 1)}
                />
                {/* One-line key for the per-point stage badges on the rows. */}
                <StageLegend />
                {/* Bottom-right per-point agent tiles: live progress + a
                    clickable "finished" card that opens the point dialog. */}
                <PipelineNotifications
                  questId={selectedQuestId}
                  runId={pipelineMeta.runId}
                  locations={locations}
                  onChange={(kind, uuid) => {
                    if (kind === 'point') { patchPipelinePoint(uuid); return; }
                    (kind === 'meta' ? setPipelineMetaRefresh : setPipelineRefresh)((k) => k + 1);
                  }}
                />
                </>
              ) : (!isReadOnly && selectedTable === 'quests_test' && selectedQuestId && (
                // Hand-made quest with no linked run: offer to attach the
                // pipeline (task cards + writer ↔ critic per point) in place.
                <div style={{
                  border: '1px dashed var(--border,#bbb)', borderRadius: 6, padding: '6px 10px',
                  margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
                }}>
                  <button
                    onClick={handleEnablePipeline}
                    style={{ background: '#1a73e8', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer' }}
                  >
                    🧩 Enable Quest Pipeline
                  </button>
                  <span style={{ opacity: 0.65 }}>
                    Adds 🃏 task cards and the writer ↔ critic loop to every location of this quest.
                  </span>
                </div>
              ))}
              <LocationsList
                lens={lens}
                locations={locations}
                selectedLocationId={selectedLocationId}
                onLocationSelect={handleLocationSelect}
                onQuestSelect={handleQuestSelect}
                selectedQuestId={selectedQuestId}
                questsList={questsList}
                currentQuestCity={currentQuestCity}
                onMoveLocationToQuest={handleMoveLocationToQuest}
                onLocationUpdate={handleLocationUpdate}
                saveStatus={saveStatus}
                onLocationReorder={handleLocationReorder}
                onAddLocation={handleAddLocation}
                onRemoveLocation={handleRemoveLocation}
                onLogout={handleLogout}
                onShowSpeechGeneration={showSpeechGenerationView}
                onExportLocationMarkdown={handleExportLocationMarkdown}
                audioRefreshToken={audioRefreshToken}
                isReadOnly={isReadOnly}
                selectedTable={selectedTable}
                commentsByLocation={commentsByLocation}
                taskCountsByLocation={taskCountsByLocation}
                tasksHref={(uuid) => buildTasksPath(selectedTable, selectedQuestId, { point: uuid })}
                currentUsername={currentUser && currentUser.username}
                onAddComment={handleAddComment}
                onEditComment={handleEditComment}
                onDeleteComment={handleDeleteComment}
                usernames={usernames}
                tagAssigneesByLocation={tagAssigneesByLocation}
                onAddTag={handleAddLocationTag}
                onRemoveTag={handleRemoveLocationTag}
                onReassignTag={handleReassignLocationTag}
                pipelinePointsByUuid={pipelineMeta ? pipelineMeta.byUuid : null}
                renderPipelineControls={pipelineMeta ? (loc) => (
                  <PipelineLocationControls
                    questId={selectedQuestId}
                    location={loc}
                    point={pipelineMeta.byUuid[loc.uuid]}
                    onChange={(kind, uuid) => {
                      if (kind === 'point') { patchPipelinePoint(uuid); return; }
                      (kind === 'meta' ? setPipelineMetaRefresh : setPipelineRefresh)((k) => k + 1);
                    }}
                  />
                ) : null}
              />
              </>
            )}
          </div>
          <div
            className="resizer" 
            onMouseDown={startResize}
            onTouchStart={startResize}
            title="Drag to resize"
          ></div>
          {showQuestInfoEditor ? (
            <AppPreview
              questId={selectedQuestId}
              selectedTable={selectedTable}
              questInfo={questInfo}
              locationsCount={locations.length}
              city={currentQuestCity}
              promoLong={previewPromo && previewPromo.promoLong}
              promoAfter={previewPromo && previewPromo.promoAfter}
              coverImageIds={previewPromo && previewPromo.coverImageIds}
              price={previewRow(previewPrice) && previewRow(previewPrice).price}
              opensAt={previewRow(previewOpensAt) && previewRow(previewOpensAt).opens_at}
              today={previewOpensAt && previewOpensAt.today}
            />
          ) : rightPanelView === 'map' ? (
            <QuestMap
              locations={locations}
              lens={lens}
              tagAssigneesByLocation={tagAssigneesByLocation}
              currentUsername={currentUser && currentUser.username}
              selectedLocationId={selectedLocationId}
              onLocationSelect={handleLocationSelect}
              onLocationMove={handleLocationMove}
              isReadOnly={isReadOnly}
              city={currentQuestCity || 'istanbul'}
            />
          ) : (
            <SpeechGeneration
              initialData={speechGenInitialData}
              onBack={showMapView}
              backHref={navHrefs.editor}
              onSaveVoiceoverText={handleSaveVoiceoverText}
              onAudioUploaded={() => setAudioRefreshToken(t => t + 1)}
            />
          )}
        </div>
        </ErrorBoundary>
      )}
      <footer className="app-footer" />
    </div>
  );
}

export default App;